import React, { useState, useEffect } from 'react';
import { FiX, FiImage, FiCheckCircle, FiAlertCircle, FiLoader, FiBox, FiTruck, FiDollarSign } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { formatTND, getTruckTypeLabel } from './utils/helpers';

const ImageAnalysisModal = ({ 
  isOpen, 
  imageUrl, 
  onClose,
  analysisData = null,
  isAnalyzing = false
}) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (isAnalyzing) {
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return Math.min(prev + 1.5, 100);
        });
      }, 50);
      return () => clearInterval(interval);
    } else {
      setProgress(100);
    }
  }, [isAnalyzing]);

  if (!isOpen || !imageUrl) return null;

  const detections = analysisData?.furnitureList || [];
  const hasDetections = detections.length > 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1300] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header moderne */}
          <div className="relative px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                  <FiImage className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Analyse d'image</h3>
                  <p className="text-sm text-gray-500">
                    {isAnalyzing ? 'Traitement en cours...' : hasDetections ? 'Analyse terminée' : 'Prêt'}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {/* Barre de progression moderne */}
            {isAnalyzing && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-600">Analyse en cours</span>
                  <span className="text-xs font-semibold text-blue-600">{Math.round(progress)}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Contenu scrollable */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Image moderne */}
              <div className="relative group">
                <div className="relative rounded-xl overflow-hidden bg-gray-100 border-2 border-gray-200 shadow-sm">
                  <img
                    src={imageUrl}
                    alt="Image analysée"
                    className="w-full h-auto object-contain max-h-[500px]"
                  />

                  {/* Overlay de détection */}
                  {analysisData?.detectionImage && (
                    <div className="absolute inset-0 pointer-events-none">
                      <img
                        src={`data:image/jpeg;base64,${analysisData.detectionImage}`}
                        alt="Détections"
                        className="w-full h-full object-contain opacity-90"
                      />
                    </div>
                  )}

                  {/* Loader moderne */}
                  {isAnalyzing && (
                    <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center">
                      <div className="text-center">
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="w-12 h-12 mx-auto mb-3"
                        >
                          <FiLoader className="w-full h-full text-blue-500" />
                        </motion.div>
                        <p className="text-sm font-medium text-gray-700">Analyse en cours...</p>
                      </div>
                    </div>
                  )}

                  {/* Badge de succès */}
                  {!isAnalyzing && hasDetections && (
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="absolute top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg"
                    >
                      <FiCheckCircle className="w-5 h-5" />
                      <span className="font-semibold text-sm">{detections.length} détecté{detections.length > 1 ? 's' : ''}</span>
                    </motion.div>
                  )}
                </div>
              </div>

              {/* Panneau de résultats moderne */}
              <div className="space-y-4">
                {isAnalyzing ? (
                  <div className="h-full flex items-center justify-center py-12">
                    <div className="text-center">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-16 h-16 mx-auto mb-4 text-blue-500"
                      >
                        <FiLoader className="w-full h-full" />
                      </motion.div>
                      <p className="text-gray-600 font-medium">Analyse en cours...</p>
                      <p className="text-sm text-gray-500 mt-2">Veuillez patienter</p>
                    </div>
                  </div>
                ) : hasDetections ? (
                  <>
                    {/* Statistiques */}
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                          <FiCheckCircle className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-gray-900">Analyse terminée</h4>
                          <p className="text-sm text-gray-600">{detections.length} meuble{detections.length > 1 ? 's' : ''} détecté{detections.length > 1 ? 's' : ''}</p>
                        </div>
                      </div>
                    </div>

                    {/* Liste des meubles */}
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                      <div className="p-4 border-b border-gray-200">
                        <h4 className="font-bold text-gray-900 flex items-center gap-2">
                          <FiBox className="w-5 h-5 text-gray-700" />
                          Meubles détectés
                        </h4>
                      </div>
                      <div className="p-4 space-y-3 max-h-72 overflow-y-auto">
                        {detections.map((item, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <h5 className="font-semibold text-gray-900 mb-1">{item.name}</h5>
                                {item.dimensions && (
                                  <p className="text-sm text-gray-600">
                                    {item.dimensions[0]} × {item.dimensions[1]} × {item.dimensions[2]} cm
                                  </p>
                                )}
                              </div>
                              {item.confidence && (
                                <div className="ml-3">
                                  <div className="bg-blue-100 text-blue-700 px-2.5 py-1 rounded-lg text-xs font-semibold">
                                    {Math.round(item.confidence * 100)}%
                                  </div>
                                </div>
                              )}
                            </div>
                            {item.quantity > 1 && (
                              <div className="text-xs text-gray-500 mt-2">
                                Quantité: {item.quantity}
                              </div>
                            )}
                          </motion.div>
                        ))}
                      </div>
                    </div>

                    {/* Recommandations */}
                    {analysisData?.suggestedTruck && (
                      <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-5 border border-amber-200">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                            <FiTruck className="w-6 h-6 text-white" />
                          </div>
                          <h4 className="font-bold text-gray-900">Recommandation</h4>
                        </div>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between py-2 border-b border-amber-200">
                            <span className="text-gray-700 font-medium">Type de camion</span>
                            <span className="font-bold text-amber-700">{getTruckTypeLabel(analysisData.suggestedTruck)}</span>
                          </div>
                          {analysisData.price && (
                            <div className="flex items-center justify-between py-2">
                              <span className="text-gray-700 font-medium flex items-center gap-2">
                                <FiDollarSign className="w-4 h-4" />
                                Estimation
                              </span>
                              <span className="font-bold text-green-600">{formatTND(analysisData.price)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="h-full flex items-center justify-center py-12">
                    <div className="text-center">
                      <FiAlertCircle className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                      <p className="text-gray-700 font-medium">Aucun meuble détecté</p>
                      <p className="text-sm text-gray-500 mt-2">Essayez avec une autre image</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ImageAnalysisModal;




